import useIsMobile from '../hooks/useIsMobile';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import appointmentService from '../services/appointmentService';
import PageHeader from '../components/ui/PageHeader';
import EmptyState from '../components/ui/EmptyState';

const STATUS_COLORS  = { realizadas: '#10b981', canceladas: '#ef4444', reprogramadas: '#f59e0b', programadas: '#3b82f6', confirmadas: '#6366f1' };
const TYPE_LABELS    = { firma: 'Firma', consulta: 'Consulta', entrega: 'Entrega', seguimiento: 'Seguimiento', interno: 'Interno' };
const TYPE_COLORS    = { firma: '#1e2a4a', consulta: '#3b82f6', entrega: '#10b981', seguimiento: '#f59e0b', interno: '#9ca3af' };
const MONTHS         = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const selectSt = { padding: '7px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8, fontSize: 13, color: '#374151', backgroundColor: '#fff', outline: 'none', cursor: 'pointer' };

const SectionCard = ({ title, children, noPadding }) => (
  <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
    <div style={{ padding: '12px 18px', borderBottom: '1px solid #F3F4F6', fontWeight: 600, fontSize: 14, color: '#111827' }}>{title}</div>
    <div style={noPadding ? {} : { padding: '16px 18px' }}>{children}</div>
  </div>
);

export default function AgendaStats() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear]   = useState(now.getFullYear());
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await appointmentService.getStats(month, year); setStats(res.data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, [month, year]);

  useEffect(() => { load(); }, [load]);

  const years = Array.from({ length: 4 }, (_, i) => now.getFullYear() - i);
  const daysInMonth = new Date(year, month, 0).getDate();
  const maxDay = stats ? Math.max(...Array.from({ length: daysInMonth }, (_, i) => stats.by_day[i + 1] || 0), 1) : 1;

  const ProgressBar = ({ label, value, total, color }) => {
    const pct = total > 0 ? Math.round((value / total) * 100) : 0;
    return (
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
          <span style={{ fontSize: 12, color: '#4B5563', fontWeight: 500 }}>{label}</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>{value} <span style={{ color: '#9CA3AF', fontWeight: 400 }}>({pct}%)</span></span>
        </div>
        <div style={{ height: 7, backgroundColor: '#F3F4F6', borderRadius: 99, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct}%`, backgroundColor: color, borderRadius: 99, transition: 'width 0.6s ease' }} />
        </div>
      </div>
    );
  };

  return (
    <div>
      <PageHeader
        title="Estadísticas de Agenda"
        subtitle={`${MONTHS[month - 1]} ${year}`}
        actions={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select value={month} onChange={e => setMonth(Number(e.target.value))} style={{ ...selectSt, minWidth: 120 }}>
              {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
            <select value={year} onChange={e => setYear(Number(e.target.value))} style={{ ...selectSt, minWidth: 80 }}>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <button onClick={() => navigate('/agenda')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 8, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', color: '#374151', fontWeight: 600, fontSize: 13, cursor: 'pointer', transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#F9FAFB'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}>
              <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              ← Volver a la Agenda
            </button>
          </div>
        }
      />

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh', flexDirection: 'column', gap: 14 }}>
          <div style={{ width: 32, height: 32, border: '3px solid #EEF0FB', borderTopColor: '#4F63D2', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
        </div>
      ) : !stats || stats.total === 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '56px 24px', gap: 10 }}>
          <span style={{ fontSize: 40 }}>📅</span>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', fontFamily: 'sans-serif' }}>
            Sin citas en {MONTHS[month - 1]} {year}
          </div>
          <div style={{ fontSize: 13, color: '#6B7280', fontFamily: 'sans-serif', textAlign: 'center', maxWidth: 320 }}>
            No se registraron citas en este período. Prueba seleccionando un mes diferente o revisa si hay citas en la agenda.
          </div>
          <button onClick={() => navigate('/agenda')}
            style={{ marginTop: 4, padding: '8px 18px', borderRadius: 8, border: 'none', backgroundColor: '#4F63D2', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'sans-serif' }}>
            Ir a la Agenda
          </button>
        </div>
      ) : (
        <>
          {/* Stat cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
            {[
              { label: 'Total de citas',    value: stats.total,         color: '#1e2a4a', bg: '#EEF0FB' },
              { label: 'Realizadas',        value: stats.realizadas,    color: '#10b981', bg: '#D1FAE5' },
              { label: 'Canceladas',        value: stats.canceladas,    color: '#ef4444', bg: '#FEE2E2' },
              { label: 'Reprogramadas',     value: stats.reprogramadas, color: '#f59e0b', bg: '#FEF3C7' },
              { label: 'Tasa completado',   value: `${stats.tasa_completado}%`, color: '#10b981', bg: '#D1FAE5' },
            ].map((card, i) => (
              <div key={i} style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', padding: '16px', borderTop: `3px solid ${card.color}` }}>
                <div style={{ fontSize: 26, fontWeight: 800, color: card.color, letterSpacing: '-0.5px', lineHeight: 1 }}>{card.value}</div>
                <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 5, fontWeight: 500 }}>{card.label}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap: 16, marginBottom: 16 }}>
            {/* Gráfica por día */}
            <SectionCard title={`Citas por día — pico: ${maxDay} cita${maxDay !== 1 ? 's' : ''}`}>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 110, overflowX: 'auto', paddingBottom: 4 }}>
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const day   = i + 1;
                  const total = stats.by_day[day] || 0;
                  const pct   = Math.round((total / maxDay) * 100);
                  const isWeekend = [0, 6].includes(new Date(year, month - 1, day).getDay());
                  return (
                    <div key={day} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 16 }}>
                      {total > 0 && <div style={{ fontSize: 9, color: '#6B7280', marginBottom: 2 }}>{total}</div>}
                      <div style={{ width: '100%', height: pct > 0 ? `${pct}%` : 3, background: pct > 0 ? (isWeekend ? '#C7D2F8' : '#4F63D2') : '#F3F4F6', borderRadius: '3px 3px 0 0', minHeight: 3, transition: 'height 0.3s' }} />
                      <div style={{ fontSize: 8, color: '#9CA3AF', marginTop: 3 }}>{day}</div>
                    </div>
                  );
                })}
              </div>
            </SectionCard>

            {/* Por tipo */}
            <SectionCard title="Por tipo de cita">
              {Object.entries(TYPE_LABELS).map(([key, label]) => (
                <ProgressBar key={key} label={label} value={stats.by_type[key] || 0} total={stats.total} color={TYPE_COLORS[key]} />
              ))}
            </SectionCard>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
            {/* Por status */}
            <SectionCard title="Por estado">
              {[
                { key: 'realizadas',    label: 'Realizadas' },
                { key: 'confirmadas',   label: 'Confirmadas' },
                { key: 'programadas',   label: 'Programadas' },
                { key: 'reprogramadas', label: 'Reprogramadas' },
                { key: 'canceladas',    label: 'Canceladas' },
              ].map(({ key, label }) => (
                <ProgressBar key={key} label={label} value={stats[key] || 0} total={stats.total} color={STATUS_COLORS[key]} />
              ))}
            </SectionCard>

            {/* Por usuario */}
            <SectionCard title="Por responsable" noPadding>
              {!stats.by_user?.length ? (
                <EmptyState icon="👤" title="Sin datos de usuarios" />
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#F9FAFB' }}>
                      {['Usuario', 'Citas', '%'].map((h, i) => (
                        <th key={i} style={{ padding: '9px 14px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9CA3AF', textAlign: i === 0 ? 'left' : 'right', borderBottom: '1px solid #F3F4F6' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...stats.by_user].sort((a, b) => b.total - a.total).map((u, i) => {
                      const pct = stats.total > 0 ? Math.round((u.total / stats.total) * 100) : 0;
                      return (
                        <tr key={i} style={{ backgroundColor: '#fff' }}
                          onMouseEnter={e => e.currentTarget.style.backgroundColor = '#FAFBFF'}
                          onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}>
                          <td style={{ padding: '10px 14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ width: 28, height: 28, borderRadius: '50%', backgroundColor: '#EEF0FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#4F63D2', fontSize: 11, flexShrink: 0 }}>
                                {u.user.charAt(0).toUpperCase()}
                              </div>
                              <span style={{ fontSize: 13, color: '#374151' }}>{u.user}</span>
                            </div>
                          </td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', fontSize: 13, fontWeight: 700, color: '#4F63D2' }}>{u.total}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                            <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 20, backgroundColor: '#F3F4F6', color: '#374151' }}>{pct}%</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </SectionCard>
          </div>
        </>
      )}
    </div>
  );
}
